import type { MechanicDefinition } from "./types";

export const economy: MechanicDefinition = {
  id: "economy",
  name: "Economy & Shops",
  description: "Merchants, buying/selling, loot tables, and trade — all inventory-based. Currencies are inventory items.",
  icon: "Coins",
  category: "systems",
  aiHints: {
    purpose: "Enables shops, buying/selling, loot tables, and trade mechanics. Currencies are inventory items (coins, credits, gems) — there is NO separate gold/currency variable.",
    whenToUse: "Any world with merchants, loot, or economic gameplay — RPGs, adventure, tycoon games.",
    synergies: ["inventory", "reputation", "quest_journal", "organization", "resources"],
    conflicts: [],
    prerequisites: ["inventory"],
    tags: ["currency", "shops", "merchants", "loot", "trade", "economy", "barter"],
    complexity: 2,
    exampleConfig: { economy_style: "simple", loot_drops: true, currency_items: "Gold Coins" },
    stateVariables: ["lifetime_transactions"],
  },
  configSchema: [
    {
      key: "currency_items",
      label: "Currency Items",
      type: "text",
      default: "Gold Coins",
      description: "Comma-separated names of items that function as currency (e.g. 'Gold Coins, Silver Coins, Gems'). These are inventory items with the 'currency' tag.",
    },
    {
      key: "economy_style",
      label: "Economy Style",
      type: "select",
      options: [
        { value: "simple", label: "Simple — flat prices, basic buy/sell", description: "Standard prices. Buy at full price, sell at 50%. No price fluctuation." },
        { value: "dynamic", label: "Dynamic — prices fluctuate by region/supply", description: "Prices vary by region, supply/demand, and reputation." },
        { value: "barter", label: "Barter — trade items for items, no currency", description: "No currency items. Players trade items directly using relative value tiers." },
      ],
      default: "simple",
    },
    {
      key: "loot_drops",
      label: "Loot Drops",
      type: "boolean",
      default: true,
      description: "Enemies drop items on defeat. Loot scales with enemy difficulty.",
    },
  ],
  systemPromptSection: (config) => {
    const style = config.economy_style || "simple";
    const loot = config.loot_drops !== false;
    const currencyItems = (config.currency_items || "Gold Coins").split(",").map((s: string) => s.trim()).filter(Boolean);
    const isBarter = style === "barter";

    return `## ECONOMY & SHOPS (Inventory-Based)
- **ALL currencies are INVENTORY ITEMS.** There is NO separate gold/currency variable.
${isBarter ? "- Style: Barter — trade items directly. No currency items." : `- Currency items: ${currencyItems.join(", ")}. These are regular inventory items with the "currency" tag and a quantity.`}
- Style: ${style === "dynamic" ? "Dynamic — prices vary by region, supply/demand, reputation." : style === "barter" ? "Barter — items have relative value tiers." : "Simple — standard prices. Buy full, sell at 50%."}
${loot ? "- Enemies drop items on defeat. Loot scales with difficulty." : ""}
- **MANDATORY**: Use manage_inventory for ALL economic transactions:
  - BUYING: manage_inventory(action:"remove", item_name:"${currencyItems[0] || "Gold Coins"}", quantity:PRICE) AND manage_inventory(action:"add", item_name:PURCHASED_ITEM)
  - SELLING: manage_inventory(action:"remove", item_name:SOLD_ITEM) AND manage_inventory(action:"add", item_name:"${currencyItems[0] || "Gold Coins"}", quantity:SELL_PRICE)
  - LOOTING: manage_inventory(action:"add") for each looted item/currency
- Use generate_shop when visiting a merchant to display inventory with prices.
- ALWAYS check the player's currency item quantity before purchases.
- Track transactions via the transaction tool for logging purposes.

### PRICE TRANSPARENCY (MANDATORY)
- Every shop/merchant interaction MUST show clear prices BEFORE any purchase.
- ALWAYS call generate_shop first to display items with costs when visiting a merchant.
- NEVER let purchases happen without the player seeing the price first.
- Format prices clearly: "Iron Sword — 45 ${currencyItems[0] || "coins"}" not vague references.
- If the player tries to buy without visiting a shop, narrate finding one first.

### NON-MONETARY PRICES
- NPCs can demand ANYTHING as payment — not just currency items:
  - Favors: "Deliver a letter and the map is yours."
  - Quests: "Bring me three moonflower petals."
  - Information: "Tell me where the rebels hide."
  - Items as barter: "I want that ring, not your coins."
  - Loyalty: "Join our guild and the equipment is free."
- Use transaction(type:"trade") with description for non-monetary costs.
- Price is CONTEXTUAL: a starving merchant values food over gold; a proud warrior values respect.

### ECONOMY ↔ INVENTORY INTEGRATION
- Currencies ARE inventory items. manage_inventory is the ONLY tool for moving currency.
- When BUYING: remove currency items, add purchased item.
- When SELLING: remove sold item, add currency items.
- Physical commodities (gold bars, salt, ammo) are inventory items with quantity and tags.

### ECONOMY ↔ STRATEGY & OPERATIONS INTEGRATION
- Organization income should add currency items to org inventory scope.
- Resource upkeep costs remove currency items from the relevant inventory scope.

### ITEM VALUE TIERS
- Common: 1-25 | Uncommon: 25-100 | Rare: 100-500 | Epic: 500-2000 | Legendary: 2000+

### LOOT GUIDELINES
- Weak enemy: 5-15 currency, 30% Common item | Standard: 15-50, 50% Common/15% Uncommon
- Elite: 50-150, 70% Uncommon/25% Rare | Boss: 200-500, guaranteed Rare/30% Epic
${style === "dynamic" ? "- Prices fluctuate: +20% remote, -15% high rep, +30% contraband." : ""}`;
  },
  widgets: [],
  initializeState: (config) => ({
    lifetime_transactions: 0,
  }),
  analyzerPrompt: (config, turnSummary) => {
    const style = config.economy_style || "simple";
    const currencyItems = config.currency_items || "Gold Coins";
    return `You are the Economy Analyzer for a narrative game.
Economy is INVENTORY-BASED. Currency items: "${currencyItems}". Style: ${style}.
All currencies are tracked as inventory items with quantity — there is NO separate gold variable.

TURN SUMMARY:
${turnSummary.brief}

TOOL RESULTS THIS TURN:
${turnSummary.toolResults.map((t: any) => `${t.tool}: ${t.result}`).join("\n") || "None"}

STATE CHANGES THIS TURN:
${JSON.stringify(turnSummary.stateChanges)}

TASKS:
1. If a transaction occurred but manage_inventory was NOT called to move currency items, note the missing sync.
2. If old-style currency_amount state variable is being used instead of inventory items, note it should be migrated.
3. Verify buy/sell transactions properly added AND removed items.

Output ONLY valid JSON (no markdown):
{ "state_patches": {}, "notes": [] }
Or output null if no changes needed.`;
  },
  onEvent: (event) => {
    if (event.type === "combat_kill") {
      return {
        narrativeNotes: ["⚔️ Enemy defeated — check for loot drops."],
      };
    }
    if (event.type === "region_enter" && event.data.region_type === "town") {
      return {
        narrativeNotes: ["🏪 Town entered — merchant shops available."],
      };
    }
    return null;
  },
  postTurnHook: (_context) => null,
  tools: [
    {
      type: "function",
      function: {
        name: "transaction",
        description: "Log an economic transaction. Currency movement itself is done via manage_inventory — this tool is for record-keeping and narrative acknowledgment.",
        parameters: {
          type: "object",
          properties: {
            type: { type: "string", enum: ["buy", "sell", "loot", "reward", "cost", "trade", "barter"] },
            items_given: { type: "array", items: { type: "object", properties: { name: { type: "string" }, quantity: { type: "number" } }, required: ["name"] }, description: "Items/currency given by the player" },
            items_received: { type: "array", items: { type: "object", properties: { name: { type: "string" }, quantity: { type: "number" } }, required: ["name"] }, description: "Items/currency received by the player" },
            merchant_name: { type: "string" },
            description: { type: "string" },
          },
          required: ["type", "description"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "generate_shop",
        description: "Generate shop inventory for current merchant. Prices are in currency items.",
        parameters: {
          type: "object",
          properties: {
            merchant_name: { type: "string" },
            shop_type: { type: "string", description: "general, weapons, armor, potions, magic, black_market" },
            region_type: { type: "string" },
            item_count: { type: "number" },
            price_modifier: { type: "number" },
          },
          required: ["merchant_name", "shop_type"],
        },
      },
    },
  ],
};
